import { toast } from "sonner";
import { getErrorMessage, toastError } from "@/lib/toast";

export const ENTITLEMENT_CODES = [
  "ENTITLEMENT_SEATS",
  "ENTITLEMENT_STORAGE",
  "ENTITLEMENT_AI_QUOTA",
  "ENTITLEMENT_KANBAN",
  "ENTITLEMENT_AUDIT",
  "ENTITLEMENT_SSO",
  "ENTITLEMENT_CONNECTORS",
];

/** Plain outcome copy for members (no plan jargon). */
export const MEMBER_ENTITLEMENT_MESSAGES = {
  ENTITLEMENT_SEATS:
    "This workplace can’t add more people right now. Contact your org admin.",
  ENTITLEMENT_STORAGE:
    "This workplace is out of storage space. Contact your org admin.",
  ENTITLEMENT_AI_QUOTA:
    "Catch-up isn’t available right now. Contact your org admin.",
  ENTITLEMENT_KANBAN:
    "This workplace can’t turn on more team boards right now. Contact your org admin.",
  ENTITLEMENT_AUDIT:
    "Audit log isn’t available on this workplace’s plan. Contact your org admin.",
  ENTITLEMENT_SSO:
    "SSO isn’t available on this workplace’s plan. Contact your org admin.",
  ENTITLEMENT_CONNECTORS:
    "Circatrons aren’t available on this workplace’s plan. Contact your org admin.",
};

/** Admin copy — same outcomes, points them to Plan & usage / Circa. */
export const ADMIN_ENTITLEMENT_MESSAGES = {
  ENTITLEMENT_SEATS:
    "This workplace can’t add more people right now — you’re at the member limit.",
  ENTITLEMENT_STORAGE:
    "This workplace is out of storage space for its current plan.",
  ENTITLEMENT_AI_QUOTA:
    "Catch-up isn’t available right now — the monthly summary limit is used up.",
  ENTITLEMENT_KANBAN:
    "This workplace can’t turn on more team boards on its current plan.",
  ENTITLEMENT_AUDIT:
    "Audit log view and export require Business or higher.",
  ENTITLEMENT_SSO:
    "SSO requires Business or higher.",
  ENTITLEMENT_CONNECTORS:
    "Circatrons require Pro or higher.",
};

/**
 * @returns {string|null}
 */
export function getEntitlementCode(err) {
  if (err?.response?.status !== 403) return null;
  const code = err?.response?.data?.code;
  if (typeof code !== "string") return null;
  if (!ENTITLEMENT_CODES.includes(code)) return null;
  return code;
}

/**
 * If err is an entitlement 403, toast the right copy and return true.
 * Otherwise toast a normal error and return false.
 *
 * @param {unknown} err
 * @param {string} [fallback]
 * @param {{ canManageSettings?: boolean, orgId?: string|null }} [ctx]
 */
export function toastEntitlementOrError(err, fallback, ctx = {}) {
  const code = getEntitlementCode(err);
  if (!code) {
    toastError(err, fallback);
    return false;
  }

  const { canManageSettings = false, orgId = null } = ctx;
  if (canManageSettings && orgId) {
    toast.error(ADMIN_ENTITLEMENT_MESSAGES[code], {
      description: "Open Plan & usage, or contact Circa to upgrade.",
      action: {
        label: "Plan & usage",
        onClick: () => {
          window.location.assign(
            `/orgs/${encodeURIComponent(orgId)}/settings?section=plan`
          );
        },
      },
    });
  } else {
    toast.error(
      MEMBER_ENTITLEMENT_MESSAGES[code] ||
        "Something couldn’t be completed. Contact your org admin."
    );
  }
  return true;
}

/**
 * Message string for inline error UIs (catch-up card, wizards).
 */
export function entitlementMessageFor(err, { canManageSettings = false } = {}) {
  const code = getEntitlementCode(err);
  if (!code) return getErrorMessage(err);
  if (canManageSettings) {
    return `${ADMIN_ENTITLEMENT_MESSAGES[code]} Contact Circa to upgrade if you need a higher plan.`;
  }
  return MEMBER_ENTITLEMENT_MESSAGES[code];
}
